// Promos de cada forma de pago: monto, una fila de chips (conserva, pierde, gana) y «Ver detalle».
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCatalog } from '@/domains/sales/wizard/api/useWizard';
import { useCatalogLineInfo } from '@/domains/sales/wizard/api/useCatalogLineInfo';
import { anchorLineOf } from '@/domains/sales/wizard/buildSale';
import { sureKeepFinances } from '@/domains/sales/wizard/cardBenefits';
import {
    creditPromotionRows,
    creditPromotionsForItem,
    type CreditPromotionRow,
} from '@/domains/sales/wizard/creditPromotions';
import { effectChips } from '@/domains/sales/wizard/promotionDetail';
import type { ReservedLine, WizardSession } from '@/domains/sales/wizard/types';
import { cn } from '@/lib/cn';
import { catalogDisplayName } from '@/shared/lib/format/itemDisplayName';
import { formatMXN } from '@/shared/lib/format/money';
import { evaluatedCodeCents, usePromotionFacts } from '../hooks/usePaymentTrackTotal';
import {
    byName,
    heldCodeIsSureKeepOnly,
    heldCodePromotionOf,
    releasedPromotionsOf,
    salePromotionsOf,
    splitForTrack,
    trackTotalCents,
    uniqueByPromotion,
    type PaymentPromotion,
    type PaymentTrack,
} from '../lib/paymentOptionPromotions';
import { releasedFor } from '../lib/sureKeepRelease';
import { useSessionPromotionState } from '../store/promotionSelectionStore';

/** Chips visibles antes de juntar el resto en «+N». */
const MAX_CHIPS = 3;

type ChipTone = 'keep' | 'lose' | 'gain';

const CHIP_TONE: Record<ChipTone, string> = {
    keep: 'border-success/40 bg-success-soft text-success',
    lose: 'border-destructive/30 bg-destructive/10 text-destructive line-through',
    gain: 'border-promo/40 bg-promo-soft text-promo',
};

interface Chip {
    key: string;
    label: string;
    tone: ChipTone;
}

const fmt = (cents: number): string => formatMXN(cents / 100);

/** Promos de crédito SureKeep del teléfono para el nivel del cliente (todas si aún no hay nivel). */
function useCreditRows(sessionId: string, session: WizardSession): CreditPromotionRow[] {
    const catalog = useCatalog(sessionId, { cached: true }).data;
    const anchor = anchorLineOf(session);
    const item = anchor && catalog?.items.find((i) => i.sku === anchor.sku);
    if (!anchor || !item || !sureKeepFinances(anchor)) return [];
    const level = session.loan?.level;
    if (level === 'NOT_ELIGIBLE') return [];
    return creditPromotionsForItem(
        creditPromotionRows(catalog?.scopeWidePromotions ?? [], [item], level),
        item,
    );
}

function ChipRow({ chips }: { chips: readonly Chip[] }) {
    if (chips.length === 0) return null;
    // Lo que se pierde nunca se esconde en «+N».
    const visible = Math.max(MAX_CHIPS, chips.filter((c) => c.tone === 'lose').length);
    const shown = chips.slice(0, visible);
    const rest = chips.slice(visible);
    return (
        <div data-testid="payment-promo-chips" className="mt-2 flex min-w-0 flex-wrap gap-1.5">
            {shown.map((chip) => (
                <span
                    key={chip.key}
                    title={chip.label}
                    data-tone={chip.tone}
                    className={cn(
                        'inline-block min-w-0 max-w-[11rem] truncate rounded-full border px-2 py-0.5 text-[11px] font-semibold',
                        CHIP_TONE[chip.tone],
                    )}
                >
                    {chip.label}
                </span>
            ))}
            {rest.length > 0 && (
                <span
                    title={rest.map((c) => c.label).join(', ')}
                    className="flex-none rounded-full border border-border px-2 py-0.5 text-[11px] font-semibold text-muted-foreground"
                >
                    +{rest.length}
                </span>
            )}
        </div>
    );
}

interface DetailRow {
    key: string;
    name: string;
    product?: string;
    /** Monto que baja; sin él, `text` dice lo que entrega. */
    amount?: string;
    text?: string;
}

/** Renglones de dos líneas del mismo alto: nombre y monto arriba, producto y lo que entrega abajo. */
function DetailList({
    title,
    tone,
    rows,
}: {
    title: string;
    tone: ChipTone;
    rows: readonly DetailRow[];
}) {
    if (rows.length === 0) return null;
    return (
        <div className="min-w-0">
            <div
                className={cn(
                    'mb-1 text-[10px] font-bold uppercase tracking-[0.06em]',
                    tone === 'keep' && 'text-success',
                    tone === 'lose' && 'text-destructive',
                    tone === 'gain' && 'text-promo',
                )}
            >
                {title}
            </div>
            <ul className="flex flex-col gap-1">
                {rows.map((row) => {
                    // En «pierde» el producto sobra: el resumen de arriba ya dice cuál es.
                    const product = tone === 'lose' ? undefined : row.product;
                    const detail = [product, row.text].filter(Boolean).join(' · ');
                    return (
                        <li key={row.key} className="min-w-0 text-[12px] leading-tight">
                            <div className="flex min-w-0 items-baseline justify-between gap-2">
                                <span
                                    className={cn(
                                        'min-w-0 truncate',
                                        tone === 'lose' && 'line-through',
                                    )}
                                    title={row.name}
                                >
                                    {row.name}
                                </span>
                                {row.amount && (
                                    <span
                                        className={cn(
                                            'flex-none font-semibold tabular-nums',
                                            tone === 'lose' && 'text-muted-foreground line-through',
                                            tone === 'gain' && 'text-promo',
                                        )}
                                    >
                                        {row.amount}
                                    </span>
                                )}
                            </div>
                            {detail !== '' && (
                                <div
                                    className="min-w-0 truncate text-[11px] text-muted-foreground"
                                    title={detail}
                                >
                                    {product && <span>{product}</span>}
                                    {product && row.text && ' · '}
                                    {row.text && <span>{row.text}</span>}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

const saleRow = (p: PaymentPromotion): DetailRow => ({
    key: `${p.promotionId}|${p.lineId}`,
    name: p.name,
    product: p.productName,
    ...(p.amountCents > 0 ? { amount: `−${fmt(p.amountCents)}` } : { text: p.text }),
});

export interface PaymentOptionPromotionsProps {
    sessionId: string;
    session: WizardSession;
    track: PaymentTrack;
    /** Total de la venta con lo que tiene apartado ahora. */
    saleTotalCents: number;
    /** Detalle siempre abierto, sin «Ver detalle». */
    alwaysOpen?: boolean;
    /** Sin el renglón del monto: el total vive en la caja de la tarjeta. */
    hideTotal?: boolean;
    /** Solo lo que gana con SureKeep o solo lo que pierde sin él; sin chips. */
    showOnly?: 'sureKeepGains' | 'sureKeepLosses';
}

export function PaymentOptionPromotions({
    sessionId,
    session,
    track,
    saleTotalCents,
    alwaysOpen = false,
    hideTotal = false,
    showOnly,
}: PaymentOptionPromotionsProps) {
    const [toggled, setOpen] = useState(false);
    const open = alwaysOpen || toggled;
    const queryClient = useQueryClient();
    const facts = usePromotionFacts(sessionId);
    const { descriptions } = useCatalogLineInfo(sessionId);
    const { sureKeepReleased, anyFinancierCodes = [] } = useSessionPromotionState(sessionId);
    const released = releasedFor(sureKeepReleased, anchorLineOf(session)?.lineId);
    const productName = (line: ReservedLine): string =>
        catalogDisplayName({ sku: line.sku, ...line.product }, descriptions);
    const code = heldCodePromotionOf(session, {
        sureKeepOnly: (c) => heldCodeIsSureKeepOnly(session, c, anyFinancierCodes),
        productName,
        evaluatedCents: (c) => evaluatedCodeCents(queryClient, sessionId, c),
    });
    const held = [...salePromotionsOf(session, { facts, productName }), ...(code ? [code] : [])];
    const promos = [
        ...held,
        ...releasedPromotionsOf(session, released, held, { facts, productName }),
    ].sort(byName);
    const credit = useCreditRows(sessionId, session);
    const { kept, lost } = splitForTrack(promos, track);
    const gained = track === 'SUREKEEP' ? credit : [];
    const totalCents = trackTotalCents(
        saleTotalCents,
        promos,
        track,
        (lineId) => session.reservedLines?.find((l) => l.lineId === lineId)?.discountCents,
    );

    const chips: Chip[] =
        showOnly !== undefined
            ? []
            : [
                  ...uniqueByPromotion(lost).map((p) => ({
                      key: `l-${p.promotionId}`,
                      label: p.name,
                      tone: 'lose' as const,
                  })),
                  ...uniqueByPromotion(kept).map((p) => ({
                      key: `k-${p.promotionId}`,
                      label: p.name,
                      tone: 'keep' as const,
                  })),
                  ...gained.map((row) => ({
                      key: `g-${row.promotionId}`,
                      label: row.name,
                      tone: 'gain' as const,
                  })),
              ];
    const hasDetail = chips.length > 0 || kept.length > 0 || lost.length > 0 || gained.length > 0;

    return (
        <div className="min-w-0">
            {track !== 'EXTERNAL' && !hideTotal && (
                <div className="mt-2 flex flex-wrap items-baseline gap-2 text-[12px]">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                        {track === 'SUREKEEP' ? 'Total' : 'Monto'}
                    </span>
                    <span className="text-[14px] font-semibold tabular-nums">
                        {fmt(totalCents)}
                    </span>
                </div>
            )}
            <ChipRow chips={chips} />
            {hasDetail && open && (
                <div className="mt-2 flex min-w-0 flex-col gap-2">
                    {showOnly === undefined && track === 'SUREKEEP' && (
                        <>
                            <DetailList
                                title="Con cualquier forma de pago"
                                tone="keep"
                                rows={kept.filter((p) => !p.sureKeepOnly).map(saleRow)}
                            />
                            <DetailList
                                title="Solo con crédito SureKeep"
                                tone="keep"
                                rows={kept.filter((p) => p.sureKeepOnly).map(saleRow)}
                            />
                        </>
                    )}
                    {showOnly === undefined && track !== 'SUREKEEP' && (
                        <DetailList title="Conserva" tone="keep" rows={kept.map(saleRow)} />
                    )}
                    {showOnly !== 'sureKeepGains' && track !== 'SUREKEEP' && (
                        <DetailList title="Lo que pierde" tone="lose" rows={lost.map(saleRow)} />
                    )}
                    {showOnly !== 'sureKeepLosses' && (
                        <DetailList
                            title="Lo que gana"
                            tone="gain"
                            rows={gained.map((row) => ({
                                key: row.promotionId,
                                name: row.name,
                                text: effectChips(row.effect)
                                    .map((c) => c.short)
                                    .join(' · '),
                            }))}
                        />
                    )}
                </div>
            )}
            {hasDetail && !alwaysOpen && (
                <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => {
                        setOpen((v) => !v);
                    }}
                    className="mt-1.5 text-[12px] text-muted-foreground underline underline-offset-2"
                >
                    {open ? 'Ocultar detalle' : 'Ver detalle'}
                </button>
            )}
        </div>
    );
}
